import React from 'react';
import classnames from 'classnames';

import {
  makeStyles,
  Button as MaterialUIButton,
  ButtonProps as MaterialUIButtonProps,
  darken,
  lighten
} from '@material-ui/core';

export type ButtonProps = Omit<MaterialUIButtonProps, 'color' | 'variant'> & {
  variant?:
    | 'inverted'
    | MaterialUIButtonProps['variant'],
  color?:
    | 'sky'
    | 'navy'
    | 'error'
    | 'lemon'
    | 'mango'
    | 'olive'
    | 'green'
    | 'orange'
    | 'purple'
    | 'success'
    | 'warning'
    | 'richBlack'
    | 'deepPurple'
    | MaterialUIButtonProps['color'],
  rel?: string,
  target?:
    | '_blank'
    | '_self'
    | '_parent'
    | '_top'
    | 'framename'
};

const useStyles = makeStyles(theme => ({
  inverted: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
      boxShadow: `0px 2px 4px ${lighten(theme.palette.grey[900], 0.2)}`
    }
  },
  invertedPrimary: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: lighten(theme.palette.primary.light, 0.6)
    }
  },
  invertedSuccess: {
    color: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.light
    }
  },
  invertedWarning: {
    color: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.light
    }
  },
  invertedError: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light
    }
  },
  containedSuccess: {
    color: theme.palette.olive.dark,
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.medium,
      boxShadow: `0px 4px 5px ${lighten(theme.palette.success.main, 0.5)}`
    }
  },
  containedWarning: {
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: lighten(theme.palette.warning.main, 0.1),
      boxShadow: `0px 4px 5px ${lighten(theme.palette.warning.main, 0.5)}`
    }
  },
  containedError: {
    color: theme.palette.error.contrastText,
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: lighten(theme.palette.error.main, 0.1),
      boxShadow: `0px 4px 5px ${lighten(theme.palette.error.main, 0.5)}`
    }
  },
  containedMango: {
    color: theme.palette.mango.contrastText,
    backgroundColor: theme.palette.mango.main,
    '&:hover': {
      backgroundColor: lighten(theme.palette.mango.main, 0.1),
      boxShadow: `0px 4px 5px ${lighten(theme.palette.mango.main, 0.5)}`
    }
  }
}));

export const Button = React.forwardRef(function Button ({
  children,
  className,
  color: colorProp,
  variant: variantProp,
  ...other
}: ButtonProps, ref: React.Ref<any>): JSX.Element {
  const classes = useStyles();
  const optionalColorProp = (colorProp === 'primary' || colorProp === 'secondary') ? { color: colorProp } : {};
  return (
    <MaterialUIButton
      ref={ref}
      variant={(variantProp === 'inverted') ? 'contained' : variantProp}
      className={classnames(
        className,
        {
          // contained
          [classes.containedMango]: variantProp === 'contained' && colorProp === 'mango',
          [classes.containedSuccess]: variantProp === 'contained' && colorProp === 'success',
          [classes.containedWarning]: variantProp === 'contained' && colorProp === 'warning',
          [classes.containedError]: variantProp === 'contained' && colorProp === 'error',
          // inverted
          [classes.inverted]: variantProp === 'inverted',
          [classes.invertedPrimary]: variantProp === 'inverted' && colorProp === 'primary',
          [classes.invertedSuccess]: variantProp === 'inverted' && colorProp === 'success',
          [classes.invertedWarning]: variantProp === 'inverted' && colorProp === 'warning',
          [classes.invertedError]: variantProp === 'inverted' && colorProp === 'error'
        }
      )}
      {...optionalColorProp}
      {...other}
    >
      {children}
    </MaterialUIButton>
  );
});

export default Button;
